import { ref, computed } from 'vue'
import { ValidationRule } from '../utils/validation'
import ToastService from '../services/Toast'

export interface FieldValidation {
  value: any
  rules: ValidationRule[]
  touched: boolean
  error: string | null
}

export function useFormValidation<T extends Record<string, any>>(initialData: T) {
  const fields = ref<Record<keyof T, FieldValidation>>({} as any)

  // Initialize fields
  Object.keys(initialData).forEach((key) => {
    fields.value[key] = {
      value: initialData[key],
      rules: [],
      touched: false,
      error: null
    }
  })

  const setFieldRules = (field: keyof T, rules: ValidationRule[]) => {
    fields.value[field].rules = rules
  }

  const clearFieldRules = (field: keyof T) => {
    fields.value[field].rules = null
  }

  const validateField = (field: keyof T) => {
    const fieldValidation = fields.value[field]
    fieldValidation.touched = true

    for (const rule of fieldValidation.rules) {
      if (!rule.validate(fieldValidation.value)) {
        fieldValidation.error = rule.message
        return false
      }
    }

    fieldValidation.error = null
    return true
  }

  const validateForm = () => {
    let isValid = true
    Object.keys(fields.value).forEach((key) => {
      if (!validateField(key)) {
        isValid = false
      }
    })
    if (!isValid) {
      // Optionally, you can set a global error message or handle it as needed
      ToastService.show('Please fix the errors in the form.', 'warning')
    }
    return isValid
  }

  const formData = computed(() => {
    const data: Partial<T> = {}
    Object.keys(fields.value).forEach((key) => {
      data[key as keyof T] = fields.value[key as keyof T].value
    })
    return data
  })

  const resetForm = () => {
    // Reset form data to initial values
    // Reset all fields
    Object.keys(fields.value).forEach((key) => {
      fields.value[key] = {
        value: initialData[key],
        rules: fields.value[key].rules,
        touched: false,
        error: null
      }
    })
  }

  const setFormData = (data: Partial<T>) => {
    Object.keys(data).forEach((key) => {
      if (fields.value[key]) {
        fields.value[key].value = data[key]
        fields.value[key].touched = true
        validateField(key as keyof T)
      }
    })
  }

  return {
    fields,
    formData,
    setFieldRules,
    validateField,
    validateForm,
    resetForm,
    clearFieldRules,
    setFormData
  }
}